import { forwardRef } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'gold';
type Size = 'sm' | 'md' | 'lg';

/*
 * La piel de primary, secondary y gold vive en index.css (`.btn-*`) y sale de
 * las variables de cada mundo: placa amarilla de seguridad en la app y el
 * panel, carmesi y oro en la portada. Aqui solo queda lo comun a los dos.
 */
const VARIANTS: Record<Variant, string> = {
  primary: 'btn-primary disabled:shadow-none',
  secondary: 'btn-secondary',
  ghost: 'text-ink-muted hover:bg-surface-2 hover:text-ink',
  // Texto en color de suelo: sobre el rojo claro del tema oscuro, el marmol
  // daba 2:1; el negro del caucho da 9:1 y el suelo de tiza sobre el rojo
  // oscuro del tema claro, 5:1.
  danger: 'bg-error text-bg ring-1 ring-inset ring-ink/10 hover:brightness-110 shadow-e1',
  gold: 'btn-gold font-semibold',
};

// Alto y radio salen del mundo (`.btn-sm/md/lg`): en el parque el tamano
// normal mide 44 px, el minimo para pulsar con el pulgar.
const SIZES: Record<Size, string> = {
  sm: 'btn-sm px-3 text-[13px] gap-1.5',
  md: 'btn-md px-4 text-sm gap-2',
  lg: 'btn-lg px-6 text-[15px] gap-2.5',
};

// `sheen` mete un destello que cruza el boton al pasar por encima: es lo que
// separa un rectangulo pintado de una pieza de metal.
const BASE =
  'sheen inline-flex items-center justify-center font-medium select-none whitespace-nowrap ' +
  'transition-[background,box-shadow,filter,transform] duration-150 active:translate-y-px ' +
  'disabled:cursor-not-allowed disabled:opacity-55 disabled:active:translate-y-0';

interface CommonProps {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

export interface ButtonProps
  extends CommonProps,
    React.ButtonHTMLAttributes<HTMLButtonElement> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    // Por defecto NO envia el formulario. Un <button> sin `type` dentro de un
    // <form> vale por un submit, asi que un boton auxiliar —cancelar, anadir
    // una fila— acabaria mandando el formulario a medio rellenar. Quien quiera
    // enviar lo pide con `type="submit"`.
    type = 'button',
    loading,
    fullWidth,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
});

interface LinkButtonProps extends CommonProps {
  to: string;
  className?: string;
  children: React.ReactNode;
  /** Enlace externo: abre en pestana nueva con rel seguro. */
  external?: boolean;
  /**
   * Con `external`: carga completa en ESTA pestana. Es lo que necesita otra
   * aplicacion de la misma web, como el panel, que no es un sitio de fuera.
   */
  sameTab?: boolean;
  onClick?: () => void;
}

export function LinkButton({
  to,
  variant = 'primary',
  size = 'md',
  fullWidth,
  className,
  children,
  external,
  sameTab,
  onClick,
}: LinkButtonProps) {
  const classes = cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className);

  if (external) {
    return (
      <a
        href={to}
        target={sameTab ? undefined : '_blank'}
        rel={sameTab ? undefined : 'noopener noreferrer'}
        className={classes}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={classes} onClick={onClick}>
      {children}
    </Link>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
